import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MusicNote, UploadSimple, Trash, SpeakerHigh, SpeakerSlash } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtBytes = (n) => {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

export const SiteMusicAdmin = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const fileRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/music/status`, { withCredentials: true });
      setStatus(r.data);
    } catch {
      setStatus({ active: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleUpload = async (e) => {
    e?.preventDefault?.();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error('Choose an audio file first');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    if (title.trim()) fd.append('title', title.trim());
    setUploading(true);
    try {
      const r = await axios.post(`${API}/admin/music/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded "${r.data.title}" (${fmtBytes(r.data.size)})`);
      setTitle('');
      if (fileRef.current) fileRef.current.value = '';
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const toggleEnabled = async () => {
    try {
      const r = await axios.post(`${API}/admin/music/toggle`, {}, { withCredentials: true });
      toast.success(r.data.enabled ? 'Music enabled' : 'Music muted site-wide');
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toggle failed');
    }
  };

  const removeTrack = async () => {
    if (!window.confirm('Remove the current site music? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/music`, { withCredentials: true });
      toast.success('Track removed');
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove track');
    }
  };

  return (
    <section className="mb-12" data-testid="site-music-admin-section">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-serif font-normal tracking-tight text-ivory flex items-center gap-3">
            <MusicNote size={22} weight="duotone" className="text-gold" /> Welcome-page Music
          </h2>
          <p className="text-sm text-bone font-sans mt-2">
            Upload a single audio track to play on loop on the public Welcome page.
            Visitors see a muted speaker icon next to the logo — they tap once to enable sound.
          </p>
        </div>
        {status?.active && (
          <button
            onClick={toggleEnabled}
            className={`text-[10px] font-medium uppercase tracking-[0.28em] px-3 py-2 rounded-md transition-colors inline-flex items-center gap-1.5 ${
              status.enabled
                ? 'text-gold border border-gold/60 bg-gold/10 hover:bg-gold/15'
                : 'text-ash border border-border hover:text-gold hover:border-gold'
            }`}
            data-testid="music-toggle-enabled"
            title={status.enabled ? 'Currently playing — click to silence' : 'Currently silenced — click to enable'}
          >
            {status.enabled ? <SpeakerHigh size={12} weight="duotone" /> : <SpeakerSlash size={12} weight="duotone" />}
            {status.enabled ? 'Enabled' : 'Disabled'}
          </button>
        )}
      </div>

      {loading ? (
        <div className="panel-luxe p-6 text-center text-ash text-sm">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Current track */}
          <div className="panel-luxe p-6">
            <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash mb-3">
              Currently Active
            </div>
            {status?.active ? (
              <div className="space-y-3" data-testid="active-track-card">
                <div className="font-serif text-xl text-ivory">{status.title || status.original_filename}</div>
                <div className="text-xs text-bone space-y-1 font-mono">
                  <div>File: {status.original_filename || '—'} <span className="uppercase text-ash">· .{status.ext}</span></div>
                  <div>Size: {fmtBytes(status.size)}</div>
                  <div>
                    Uploaded:{' '}
                    {status.uploaded_at ? new Date(status.uploaded_at).toLocaleString() : '—'}
                  </div>
                </div>
                <audio
                  controls
                  preload="none"
                  src={`${API}/music/stream`}
                  className="w-full mt-3"
                  data-testid="admin-preview-audio"
                >
                  Your browser does not support audio playback.
                </audio>
                <button
                  onClick={removeTrack}
                  className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash hover:text-rose-400 px-3 py-2 rounded-md hover:bg-stone transition-colors inline-flex items-center gap-1.5"
                  data-testid="remove-track-btn"
                >
                  <Trash size={12} weight="duotone" /> Remove track
                </button>
              </div>
            ) : (
              <div className="text-sm text-ash py-6 text-center" data-testid="no-active-track">
                No track active. Upload one on the right to enable Welcome-page music.
              </div>
            )}
          </div>

          {/* Upload */}
          <form onSubmit={handleUpload} className="panel-luxe p-6 space-y-4" data-testid="music-upload-form">
            <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-ash mb-1">
              {status?.active ? 'Replace track' : 'Upload track'}
            </div>
            <div>
              <label className="block text-xs text-bone mb-1.5">Display title (optional)</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Manuscript Polish · House Theme"
                className="w-full bg-stone/40 border border-border rounded-lg px-3 py-2 text-sm text-ivory placeholder:text-ash/60 focus:outline-none focus:border-gold transition-colors"
                data-testid="music-title-input"
              />
            </div>
            <div>
              <label className="block text-xs text-bone mb-1.5">Audio file</label>
              <input
                ref={fileRef}
                type="file"
                accept="audio/mpeg,audio/mp4,audio/wav,audio/ogg,audio/aac,.mp3,.mp4,.m4a,.wav,.ogg,.aac"
                className="w-full text-sm text-bone file:mr-3 file:px-3 file:py-2 file:rounded-md file:border-0 file:bg-gold file:text-charcoal file:font-medium file:cursor-pointer hover:file:bg-gold-dark"
                data-testid="music-file-input"
              />
              <p className="text-[11px] text-ash mt-2">
                MP3 · MP4 · M4A · WAV · OGG · AAC — max 25 MB.
              </p>
            </div>
            <button
              type="submit"
              disabled={uploading}
              className="rounded-lg font-medium text-xs uppercase tracking-[0.28em] px-5 py-3 bg-gold text-charcoal hover:bg-gold-dark disabled:opacity-60 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
              data-testid="music-upload-btn"
            >
              <UploadSimple size={14} weight="duotone" />
              {uploading ? 'Uploading...' : status?.active ? 'Replace track' : 'Upload track'}
            </button>
          </form>
        </div>
      )}
    </section>
  );
};
